import React, {
	memo,
	useCallback,
	useMemo,
	useRef,
	useState
} from 'react';
import PropTypes from 'prop-types';
import {
	Icon,
	Form,
	Input
} from 'semantic-ui-react';

import { log } from '../../services/utils';

import useToggle from '../../hooks/useToggle';

import ValidationPopup from './ValidationPopup';

const FormInput = ( {
	align = 'left',
	errors = [],
	fieldLabel,
	forceValidation,
	name,
	onChange,
	readOnly,
	required,
	value,
	width,
	...props
} ) => {
	const inputRef = useRef( null );

	const [ validate, setValidate ] = useState( false );
	const [ validationPopup, setValidationPopup ] = useState( false );
	const [ showPassword, toggleShowPassword ] = useToggle( false );

	const hasFocus = useCallback( () => inputRef.current === document.activeElement, [] );
	const isInvalid = useCallback(
		() => validate || forceValidation
			? errors.some( error => error.error )
			: false,
		[
			errors,
			forceValidation,
			validate
		]
	);

	const handleChange = useCallback( (
		e,
		{
			name,
			value
		}
	) => {
		// disable validation while user is entering a value
		if ( validate ) {
			setValidate( false );
		}

		// disable validationPopup while user is entering a value
		if ( validationPopup ) {
			setValidationPopup( false );
		}

		// unmask value
		const target = {
			name,
			value
		};

		// update formData
		onChange( { target } );
	}, [
		onChange,
		validate,
		validationPopup
	] );

	const handleFocus = useCallback( () => {
		if (
			!validationPopup
			&& isInvalid()
			&& hasFocus()
		) {
			setValidationPopup( true );
		} else if ( validationPopup ) {
			setValidationPopup( false );
		}
	}, [
		hasFocus,
		isInvalid,
		validationPopup
	] );

	const handleBlur = useCallback( () => {
		// enable validation when field loses focus
		if ( errors.length && !validate ) {
			setValidate( true );
		}

		// disable validationPopup when field loses focus
		if ( validationPopup ) {
			setValidationPopup( false );
		}
	}, [
		errors.length,
		validate,
		validationPopup
	] );

	const alignment = useMemo(
		() => align === 'right'
			? { marginLeft: 'auto' }
			: null,
		[ align ]
	);

	return (
		<>
			{ log( `- ${ name } input` ) }
			<Form.Field
				id={ name }
				error={ isInvalid() }
				required={ required }
				style={ alignment }
				width={ width }
			>
				<label>{ fieldLabel }</label>
				<Input
					labelPosition="left"
					icon
					name={ name }
					onBlur={ handleBlur }
					onFocus={ handleFocus }
					onChange={ handleChange }
					readOnly={ readOnly }
					transparent={ readOnly }
					value={ value	}
					{ ...props }
					autoComplete="new-password"
				>
					<input
						ref={ inputRef }
						type={ showPassword
							? ''
							: 'password' }
					/>
					<Icon
						link
						name={ showPassword
							? 'hide'
							: 'eye'
						}
						onClick={ toggleShowPassword }
					/>
				</Input>
			</Form.Field>
			<ValidationPopup
				context={ inputRef }
				errors={ errors }
				open={ validationPopup }
			/>
		</>
	);
};

FormInput.propTypes = {
	align: PropTypes.string,
	errors: PropTypes.arrayOf( PropTypes.shape( {
		error: PropTypes.bool,
		message: PropTypes.node
	} ) ),
	fieldLabel: PropTypes.node.isRequired,
	forceValidation: PropTypes.bool,
	name: PropTypes.string.isRequired,
	onChange: PropTypes.func,
	readOnly: PropTypes.bool,
	required: PropTypes.bool,
	value: PropTypes.string.isRequired,
	width: PropTypes.number
};

export default memo( FormInput );
